import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, Loader2, Gift, User, Building2, Briefcase, MailCheck, Send } from 'lucide-react';
import Logo from '../components/common/Logo';
import LocaleSelector from '../components/common/LocaleSelector';
import PhoneInput from '../components/common/PhoneInput';
import useAuthStore from '../store/useAuthStore';
import { API } from '../utils/api';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [nif, setNif] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [accountType, setAccountType] = useState('agent'); // 'agent' | 'agency' | 'particular'
  const [requestedQrQuota, setRequestedQrQuota] = useState('');
  // Registration is a 2-step wizard: 1 = select account type, 2 = fill data.
  // The login flow ignores this state entirely.
  const [registerStep, setRegisterStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [googleEnabled, setGoogleEnabled] = useState(false);
  // Resend-verification state — surfaced when the backend returns the
  // "Email não verificado" error so the user can request a new email
  // without going through "Esqueci a password".
  const [showResend, setShowResend] = useState(false);
  const [resendStatus, setResendStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
  const [resendMessage, setResendMessage] = useState('');
  // Surface a "criar conta" CTA when login fails with invalid credentials —
  // covers the most common user mistake of trying to log in before signing
  // up.
  const [showRegisterCta, setShowRegisterCta] = useState(false);
  // Live "email already taken" status during registration. The submit
  // validation in the backend still runs — this is a UX layer so the user
  // sees the conflict immediately on blur instead of after clicking "Criar".
  const [emailStatus, setEmailStatus] = useState({ state: 'idle', provider: null });
  const { login, register } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const ref = searchParams.get('ref');
    const tab = searchParams.get('tab');
    const plan = searchParams.get('plan');
    
    if (ref) {
      setReferralCode(ref);
      setIsLogin(false);
    }
    if (tab === 'register') {
      setIsLogin(false);
    }
    if (plan) {
      setSelectedPlan(plan);
    }
  }, [searchParams]);

  // Fetch whether the admin has enabled Google OAuth — controls the visibility
  // of the "Continue with Google" button. This is the same endpoint used by
  // the /auth/google callback page so we stay consistent.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/auth/google-config`);
        if (!cancelled) setGoogleEnabled(!!res.data?.enabled);
      } catch {
        if (!cancelled) setGoogleEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH. The backend derives the redirect_uri from the
    // request Origin header which is set automatically by the browser.
    window.location.href = `${API}/auth/google/login`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowResend(false);
    setShowRegisterCta(false);
    setResendStatus('idle');
    setResendMessage('');

    // Phone is mandatory on registration
    if (!isLogin) {
      // Password must be at least 8 characters
      if (!password || password.length < 8) {
        setError('A password deve ter pelo menos 8 caracteres.');
        return;
      }
      const digits = (phone || '').replace(/[^\d]/g, '');
      // Need at least the dial code (1-3 digits) + at least 6 phone digits
      if (!phone || digits.length < 7) {
        setError('Número de telefone é obrigatório.');
        return;
      }
    }

    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        navigate('/dashboard');
      } else {
        const result = await register(
          email,
          password,
          name,
          phone,
          referralCode,
          nif,
          companyName,
          accountType,
          accountType === 'agency' && requestedQrQuota ? parseInt(requestedQrQuota, 10) : undefined
        );

        // Backend requires email verification before login — show success screen
        if (result && result.email_verification_required) {
          setRegisteredEmail(email);
          setLoading(false);
          return;
        }

        // Otherwise (already authenticated) go to dashboard
        navigate('/dashboard');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro na autenticação';
      setError(detail);
      // Detect the "email not verified" case → expose the resend CTA.
      if (
        isLogin &&
        typeof detail === 'string' &&
        /email não verificado|email nao verificado|not verified/i.test(detail)
      ) {
        setShowResend(true);
      }
      // For generic "invalid credentials" on login (in any wording the
      // backend may return), surface a "create account" CTA so a user who
      // mistyped their email or never registered can switch to the
      // register tab without losing the typed email.
      if (
        isLogin &&
        typeof detail === 'string' &&
        /credenciais inv|email ou password|invalid|inv[áa]lid|incorret/i.test(detail)
      ) {
        setShowRegisterCta(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setResendStatus('error');
      setResendMessage('Indica o teu email antes de pedir o reenvio.');
      return;
    }
    setResendStatus('sending');
    setResendMessage('');
    try {
      const res = await axios.post(`${API}/auth/resend-verification`, { email });
      setResendStatus('sent');
      setResendMessage(
        res?.data?.message ||
          'Se o email existir, enviámos um novo link de verificação. Verifica a caixa de entrada (e a pasta de Spam).'
      );
    } catch (err) {
      const detail = err.response?.data?.detail || 'Não foi possível reenviar o email. Tenta novamente.';
      // 429 — cooldown
      if (err.response?.status === 429) {
        setResendStatus('error');
        setResendMessage(detail);
      } else {
        setResendStatus('error');
        setResendMessage(detail);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      {/* Locale Selector - Fixed top right */}
      <div className="fixed top-4 right-4 z-50">
        <LocaleSelector variant="compact" />
      </div>

      {/* Success screen after registration (email verification required) */}
      {registeredEmail ? (
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <a href="/" className="inline-block">
              <Logo className="w-48" />
            </a>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <MailCheck className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Registo efetuado com sucesso!
            </h2>
            <p className="text-gray-600 text-sm mb-1">
              Enviámos um email de confirmação para:
            </p>
            <p className="text-gray-900 font-medium mb-4 break-all">
              {registeredEmail}
            </p>
            <p className="text-gray-600 text-sm mb-6">
              Clique no link presente no email para ativar a sua conta.
              Se não o encontrar na caixa de entrada, verifique a pasta de Spam/Lixo.
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => {
                  setRegisteredEmail('');
                  setIsLogin(true);
                  setPassword('');
                }}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition"
              >
                Ir para Entrar
              </button>
              <Link
                to="/"
                className="block w-full py-3 text-center text-blue-600 hover:text-blue-700 font-medium"
              >
                ← Voltar à página inicial
              </Link>
            </div>
          </div>
        </div>
      ) : (
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <a href="/" className="inline-block">
            <Logo className="w-48" />
          </a>
          <p className="text-gray-500">Plataforma de QR Codes para Imóveis</p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => { setIsLogin(true); setRegisterStep(1); }}
              className={`flex-1 py-2 text-center rounded-md transition font-medium ${isLogin ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
              data-testid="auth-tab-login"
            >
              Entrar
            </button>
            <button
              onClick={() => { setIsLogin(false); setRegisterStep(1); }}
              className={`flex-1 py-2 text-center rounded-md transition font-medium ${!isLogin ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
              data-testid="auth-tab-register"
            >
              Registar
            </button>
          </div>

          {/* Google OAuth — visible when admin enabled it. Single button covers
              both Login and Register flows (backend creates the user if new). */}
          {googleEnabled && (
            <>
              <button
                type="button"
                onClick={startGoogleLogin}
                data-testid="auth-google-button"
                className="w-full flex items-center justify-center gap-3 py-3 mb-4 bg-white hover:bg-gray-50 border border-gray-300 rounded-xl text-gray-700 font-medium transition shadow-sm"
              >
                <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.7 4.7-6.2 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.3 29.5 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.2-.1-2.3-.4-3.5z" />
                  <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.3 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
                  <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.5l-6.5-5.3c-2 1.5-4.6 2.4-7.5 2.4-5.1 0-9.4-3.2-11.1-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z" />
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.5 4.4-4.7 5.8l6.5 5.3C40.4 36.5 44 30.8 44 24c0-1.2-.1-2.3-.4-3.5z" />
                </svg>
                Continuar com Google
              </button>

              <div className="relative my-4 flex items-center" aria-hidden="true">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="px-3 text-xs uppercase tracking-wider text-gray-400">ou</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                {/* Step indicator — visible during registration only. */}
                <div className="flex items-center justify-center gap-2 mb-4" data-testid="auth-register-stepper">
                  <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${registerStep === 1 ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${registerStep === 1 ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'}`}>{registerStep === 1 ? '1' : '✓'}</span>
                    Tipo
                  </div>
                  <div className={`h-px w-8 ${registerStep === 2 ? 'bg-blue-300' : 'bg-gray-200'}`} />
                  <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${registerStep === 2 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${registerStep === 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-white'}`}>2</span>
                    Dados
                  </div>
                </div>
              </>
            )}

            {/* STEP 1 — choose account type */}
            {!isLogin && registerStep === 1 && (
              <>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">
                    Que tipo de conta vai criar?
                  </p>
                  <div className="space-y-2">
                    {[
                      { id: 'agent', label: 'Agente Imobiliário', icon: User, desc: 'Profissional independente que gere os seus próprios imóveis.' },
                      { id: 'agency', label: 'Agência', icon: Building2, desc: 'Empresa com equipa de agentes. Requer aprovação manual.' },
                      { id: 'particular', label: 'Particular', icon: Briefcase, desc: 'Proprietário a vender ou arrendar a sua casa.' },
                    ].map(({ id, label, icon: Icon, desc }) => {
                      const active = accountType === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setAccountType(id)}
                          className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 transition text-left ${
                            active
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                          data-testid={`account-type-${id}`}
                        >
                          <div className={`p-2 rounded-lg shrink-0 ${active ? 'bg-blue-100' : 'bg-gray-100'}`}>
                            <Icon className={`w-5 h-5 ${active ? 'text-blue-600' : 'text-gray-500'}`} />
                          </div>
                          <div className="flex-1">
                            <p className={`font-semibold text-sm ${active ? 'text-blue-900' : 'text-gray-900'}`}>{label}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                          </div>
                          {active && (
                            <div className="shrink-0 w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">✓</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setRegisterStep(2)}
                  data-testid="auth-register-next"
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition flex items-center justify-center gap-2"
                >
                  Continuar →
                </button>
              </>
            )}

            {/* STEP 2 — fill data (only fields relevant to chosen type) */}
            {!isLogin && registerStep === 2 && (
              <>
                <button
                  type="button"
                  onClick={() => setRegisterStep(1)}
                  data-testid="auth-register-back"
                  className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  ← Voltar (mudar tipo: <strong className="text-blue-600">{accountType === 'agent' ? 'Agente' : accountType === 'agency' ? 'Agência' : 'Particular'}</strong>)
                </button>

                <input
                  type="text"
                  placeholder={accountType === 'agency' ? 'Nome do Responsável' : 'Nome completo'}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  required
                />
              </>
            )}

            {/* Email + Password + remaining fields — shown for login, or
                registration step 2. Hidden during the registration
                "select type" step. */}
            {(isLogin || registerStep === 2) && (
              <>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                // Reset live-check state while typing so a stale "taken"
                // hint doesn't linger when the user fixes their input.
                if (emailStatus.state !== 'idle') setEmailStatus({ state: 'idle', provider: null });
              }}
              onBlur={async () => {
                if (isLogin) return;
                const candidate = (email || '').trim().toLowerCase();
                if (!candidate || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate)) return;
                setEmailStatus({ state: 'checking', provider: null });
                try {
                  const res = await axios.get(`${API}/auth/check-email`, { params: { email: candidate } });
                  if (res.data?.exists) {
                    setEmailStatus({ state: 'taken', provider: res.data?.auth_provider || null });
                  } else {
                    setEmailStatus({ state: 'available', provider: null });
                  }
                } catch (_) {
                  setEmailStatus({ state: 'idle', provider: null });
                }
              }}
              data-testid="auth-email-input"
              className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 ${
                !isLogin && emailStatus.state === 'taken'
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                  : 'border-gray-200 focus:border-blue-500 focus:ring-blue-100'
              }`}
              required
            />
            {!isLogin && emailStatus.state === 'taken' && (
              <div
                className="-mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2"
                data-testid="auth-email-taken-warning"
              >
                <span className="font-medium">Este email já está registado.</span>
                <button
                  type="button"
                  onClick={() => { setIsLogin(true); setError(''); }}
                  className="underline hover:text-red-800"
                  data-testid="auth-email-taken-go-login"
                >
                  Fazer login{emailStatus.provider === 'google' ? ' com Google' : emailStatus.provider === 'both' ? ' ou usar Google' : ''}
                </button>
              </div>
            )}
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Palavra-passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                required
                minLength={isLogin ? undefined : 8}
                data-testid="auth-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {!isLogin && (
              <p className={`text-xs -mt-2 ${password && password.length < 8 ? 'text-red-500' : 'text-gray-500'}`} data-testid="auth-password-hint">
                Mínimo 8 caracteres
              </p>
            )}
            {!isLogin && (
              <>
                <PhoneInput
                  value={phone}
                  onChange={setPhone}
                  placeholder="Número de telefone"
                  label="Telefone"
                  required
                  defaultCountry="PT"
                />
                
                {/* Billing Information */}
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-2">
                    {accountType === 'agency' ? 'Dados da Agência' : 'Dados de Faturação'}
                  </p>
                  <input
                    type="text"
                    placeholder="NIF (9 dígitos)"
                    value={nif}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                      setNif(value);
                    }}
                    pattern="[0-9]{9}"
                    maxLength={9}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 mb-2"
                  />
                  <input
                    type="text"
                    placeholder={accountType === 'agency' ? 'Nome da Agência' : 'Nome da Empresa (se aplicável)'}
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required={accountType === 'agency'}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                  {accountType === 'agency' && (
                    <input
                      type="number"
                      min="1"
                      placeholder="Quota de QR Codes pretendida (opcional)"
                      value={requestedQrQuota}
                      onChange={(e) => setRequestedQrQuota(e.target.value)}
                      className="w-full mt-2 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  )}
                </div>
                
                <div className="relative">
                  <Gift className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Código de referência (10% desconto)"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              </>
            )}

            </>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm">
                <p className="text-red-600">{error}</p>

                {showResend && (
                  <div className="mt-3 pt-3 border-t border-red-200">
                    {resendStatus === 'sent' ? (
                      <p className="text-green-700 flex items-start gap-2">
                        <MailCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{resendMessage}</span>
                      </p>
                    ) : (
                      <>
                        <p className="text-gray-700 mb-2">
                          Não recebeste o email? Podemos enviar um novo link de
                          verificação para <strong>{email || 'o teu email'}</strong>.
                        </p>
                        <button
                          type="button"
                          onClick={handleResendVerification}
                          disabled={resendStatus === 'sending' || !email}
                          className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {resendStatus === 'sending' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                          {resendStatus === 'sending'
                            ? 'A reenviar…'
                            : 'Reenviar email de verificação'}
                        </button>
                        {resendStatus === 'error' && resendMessage && (
                          <p className="mt-2 text-red-600 text-xs">{resendMessage}</p>
                        )}
                      </>
                    )}
                  </div>
                )}

                {showRegisterCta && !showResend && (
                  <div className="mt-3 pt-3 border-t border-red-200" data-testid="auth-register-cta">
                    <p className="text-gray-700 mb-2 text-sm">
                      Ainda não tens conta? Cria uma com{' '}
                      <strong className="break-all">{email || 'este email'}</strong>.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setIsLogin(false);
                        setError('');
                        setShowRegisterCta(false);
                      }}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition"
                      data-testid="auth-switch-to-register"
                    >
                      Criar conta
                    </button>
                  </div>
                )}
              </div>
            )}

            {(isLogin || registerStep === 2) && (
              <button
                type="submit"
                disabled={loading || (!isLogin && emailStatus.state === 'taken')}
                data-testid="auth-submit-button"
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-xl font-medium transition flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                {isLogin ? 'Entrar' : 'Criar Conta'}
              </button>
            )}
            
            {isLogin && (
              <div className="text-center">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Esqueceu a palavra-passe?
                </Link>
              </div>
            )}
          </form>

          {/* Link para voltar à página inicial */}
          <div className="mt-6 pt-4 border-t border-gray-100 text-center">
            <Link to="/" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              ← Voltar à página inicial
            </Link>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default AuthPage;
